import React from 'react';
import { match, P } from 'ts-pattern';

import { UseFormWithErrorsReturn } from '@/hooks/useFormWithErrors';
import type { paymentMethodFiatSchema } from '@/schemas/paymentMethod';
import { COPAccountDetailsFields } from '@/Screens/Dashboard/Contacts/COPAccountDetailsFields';
import { CVUAccountDetailsFields } from '@/Screens/Dashboard/Contacts/CVUAccountDetailsFields';
import { PIXAccountDetailsFields } from '@/Screens/Dashboard/Contacts/PIXAccountDetailsFields';
import { SpeiAccountDetailsFields } from '@/Screens/Dashboard/Contacts/SpeiAccountDetailsFields';
import { SwiftAccountDetailsFields } from '@/Screens/Dashboard/Contacts/SwiftAccountDetailsFields';
import { USDAccountFields } from '@/Screens/Dashboard/Contacts/USDAccountFields';
import { USPaymentMethodSelector } from '@/Screens/Dashboard/Contacts/USPaymentMethodSelector';
import { FiatAccountType } from '@/types/fiatAccountType';

type PaymentMethodForm = typeof paymentMethodFiatSchema.infer;

interface Props {
  readonly routingNumber?: string;
  readonly form: UseFormWithErrorsReturn<PaymentMethodForm>;
}

export const FiatAccountDetailFields: React.FC<Props> = (props: Props): React.ReactNode => {
  const { form } = props;
  const { watch, register, control, setValue } = form;
  const [paymentMethod] = watch(['paymentMethod']);

  const onPaymentMethodChange = React.useCallback(
    (
      paymentMethod:
        | FiatAccountType.ach
        | FiatAccountType.wire
        | FiatAccountType.swift
        | FiatAccountType.spei
        | FiatAccountType.pix
        | FiatAccountType.cop
        | FiatAccountType.cvu,
    ): void => {
      setValue('paymentMethod', paymentMethod);
    },
    [setValue],
  );

  return match<FiatAccountType, React.ReactNode>(paymentMethod)
    .with(
      P.union(FiatAccountType.ach, FiatAccountType.wire),
      (value: FiatAccountType.ach | FiatAccountType.wire): React.ReactNode => (
        <>
          <USPaymentMethodSelector defaultValue={value} onChange={onPaymentMethodChange} />
          <USDAccountFields
            register={register}
            watch={watch}
            setValue={setValue}
            control={control}
          />
        </>
      ),
    )
    .with(
      FiatAccountType.pix,
      (): React.ReactNode => <PIXAccountDetailsFields register={register} />,
    )
    .with(FiatAccountType.spei, (): React.ReactNode => <SpeiAccountDetailsFields form={form} />)
    .with(
      FiatAccountType.cop,
      (): React.ReactNode => (
        <COPAccountDetailsFields register={register} watch={watch} control={control} />
      ),
    )
    .with(
      FiatAccountType.cvu,
      (): React.ReactNode => (
        <CVUAccountDetailsFields register={register} setValue={setValue} watch={watch} />
      ),
    )
    .with(FiatAccountType.swift, (): React.ReactNode => {
      return (
        <SwiftAccountDetailsFields
          register={register}
          watch={watch}
          control={control}
          setValue={setValue}
        />
      );
    })
    .with(FiatAccountType.unset, (): React.ReactNode => null) // This should never happen.
    .otherwise((): React.ReactNode => null);
};
